import { useEffect, useState } from 'react';
import { type Alert, capitalize, fmtDate, greeting, localToday, plural, toMinutes } from '@quadro/domain';
import { Icon } from '../../components/Icon';
import { openAgendaEvent, useAgendaEvents } from '../agenda/agendaEvents';
import { useUI } from '../../state/ui';
import { useWorkspace } from '../../state/workspace';

const START = 7 * 60, END = 21 * 60;
const pct = (m: number) => Math.min(100, Math.max(0, ((m - START) / (END - START)) * 100));

/** Data, saudação, próximo compromisso, linha do dia e pontos de atenção. */
export function DayHeader({ alerts }: { alerts: Alert[] }) {
  const ws = useWorkspace();
  const { insights, set } = useUI();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60_000); return () => clearInterval(t); }, []);
  const today = localToday();
  const mNow = now.getHours() * 60 + now.getMinutes();
  // Equipe + Outlook de hoje; dia inteiro não entra na linha nem no "a seguir".
  const events = useAgendaEvents(today, today).filter(e => e.date === today && !e.allDay).sort((a, b) => a.start.localeCompare(b.start));
  const next = events.find(e => toMinutes(e.end) > mNow);

  return (
    <div className="dayhead">
      <div className="dh-row">
        <div className="dh-date">
          <span className="dh-wd">{capitalize(fmtDate(today, { weekday: 'long' }))}</span>
          <h1 className="dh-d">{fmtDate(today, { day: 'numeric', month: 'long' })}</h1>
          <span className="dh-greet">{greeting(now.getHours())}, {ws.firstName(ws.me.id)}</span>
        </div>
        <div className="dh-agenda">
          <span className="dh-ic"><Icon name="cal" /></span>
          {next
            ? <button className="tl-next" onClick={() => openAgendaEvent(next)}>
                <span className="muted">{toMinutes(next.start) <= mNow ? 'Agora' : 'A seguir'}, {next.start}</span> <span className="t">{next.title}</span>
                {next.location && <span className="muted">, {next.location}</span>}
              </button>
            : <span className="muted">{events.length ? 'Sem mais compromissos hoje' : 'Agenda livre hoje'}</span>}
        </div>
        {alerts.length
          ? <button className="attn" aria-pressed={insights} onClick={() => set({ insights: !insights })}><i />{plural(alerts.length, 'ponto de atenção', 'pontos de atenção')}</button>
          : <span className="ok-txt">Tudo em dia</span>}
      </div>
      <div className="tl2" aria-label="Linha do dia, das 7h às 21h">
        <div className="tl2-track">
          <span className="tl2-pass" style={{ width: `${pct(mNow)}%` }} />
          {[9, 12, 15, 18].map(h => <span key={h} className="tl2-tick" style={{ left: `${pct(h * 60)}%` }}><b>{h}h</b></span>)}
          {events.map(e => (
            <button key={e.id} className={`tl-ev ${e === next ? 'next' : ''} ${toMinutes(e.end) <= mNow ? 'past' : ''}`}
              style={{ left: `${pct(toMinutes(e.start))}%`, width: `max(6px, ${pct(toMinutes(e.end)) - pct(toMinutes(e.start))}%)` }}
              title={`${e.start}–${e.end} ${e.title}${e.external ? ' · Outlook' : ''}`} aria-label={`${e.start}, ${e.title}`} onClick={() => openAgendaEvent(e)} />
          ))}
          {mNow >= START && mNow <= END && <span className="tl-now" style={{ left: `${pct(mNow)}%` }} />}
        </div>
      </div>
    </div>
  );
}
